import React, { useState, useCallback, useMemo } from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar, closeSnackbar, SnackbarKey } from 'notistack';
import { Button } from '@mui/material';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { TicketStatus, TicketType } from '@marageti/z4-sdk/lib/tickets';
import { useAuthStore } from '../store';
import { TicketFormData } from '../pages/tickets/options';
import {
  AssigneeOption,
  teamMemberSearchRequest,
  formatTeamMembersAsOptions,
} from '../utils/team-members';

interface UseTicketSummaryActionsOptions {
  refetch: () => void;
  refetchTicketCount?: () => void;
  tripRequestId?: string;
  travelerId?: string;
  travelerName?: string;
  travelerEmail?: string;
}

const useTicketSummaryActions = ({
  refetch,
  refetchTicketCount,
  tripRequestId,
  travelerId,
  travelerName,
  travelerEmail,
}: UseTicketSummaryActionsOptions) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const { loggedInTeamMember } = useAuthStore();

  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(
    apiClient.peopleClient.postTeamMemberSearch,
    [teamMemberSearchRequest],
    true,
  );

  const assignees = useMemo<AssigneeOption[]>(() => {
    const options = formatTeamMembersAsOptions(teamMembers, []);
    return options.filter((option) => option.value);
  }, [teamMembers]);

  const [editModalOpen, setEditModalOpen] = useState(false);

  const create = useCallback(() => {
    setEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
  }, []);

  const handleEditModalSave = useCallback(
    async (data: TicketFormData) => {
      try {
        const created = await apiClient.ticketsClient.createTicket({
          title: data.title,
          description: data.description,
          ticketType: data.type?.ticketType as TicketType,
          ticketSubType: data.type?.value,
          status: data.status.value as TicketStatus,
          travelerId: data.traveler?.id || '',
          travelerName: data.traveler?.fullName || '',
          assignees: data.assignees.map((a) => ({
            id: a.value,
            name: a.title,
          })),
          tripRequestId: data.tripRequest?.value,
          tripMatchId: data.tripMatch?.value || '',
        });
        enqueueSnackbar('Ticket created.', {
          variant: 'success',
          action: (key: SnackbarKey) => (
            <Button
              href={`/tickets/${created.id}`}
              color="inherit"
              onClick={() => closeSnackbar(key)}
            >
              View
            </Button>
          ),
        });
        handleEditModalClose();
        refetch();
        refetchTicketCount?.();
      } catch {
        enqueueSnackbar('Failed to create ticket.', { variant: 'default' });
      }
    },
    [enqueueSnackbar, handleEditModalClose, refetch, refetchTicketCount, apiClient],
  );

  const defaultValues = useMemo(() => ({
    assigneeIds: loggedInTeamMember?.id ? [loggedInTeamMember.id] : [],
    ...(travelerId && { travelerId }),
    ...(travelerName && { travelerName }),
    ...(travelerEmail && { travelerEmail }),
    ...(tripRequestId && { tripRequestId }),
  }), [loggedInTeamMember?.id, travelerId, travelerName, travelerEmail, tripRequestId]);

  const editModalProps = useMemo(() => ({
    open: editModalOpen,
    onClose: handleEditModalClose,
    onSave: handleEditModalSave,
    assignees,
    defaultValues,
    isNew: true as const,
  }), [
    editModalOpen,
    handleEditModalClose,
    handleEditModalSave,
    assignees,
    defaultValues,
  ]);

  return {
    create,
    editModalProps,
  };
};

export default useTicketSummaryActions;
